import { useEffect, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress, Button, Alert } from '@mui/material';
import { CameraAlt, CheckCircle, Error as ErrorIcon, Refresh } from '@mui/icons-material';
import { useFaceDetection } from '../../hooks/useFaceDetection';

interface FaceCaptureProps {
  onCapture: (embedding: number[]) => void;
  onError?: (error: string) => void;
  autoStart?: boolean;
  captureButtonText?: string;
  showPreview?: boolean;
}

export function FaceCapture({
  onCapture,
  onError,
  autoStart = true,
  captureButtonText = 'Capture Face',
  showPreview = true
}: FaceCaptureProps) {
  const {
    isModelLoaded,
    isLoading,
    error: modelError,
    videoRef,
    canvasRef,
    startCamera,
    stopCamera,
    detectFace,
    getEmbeddingArray
  } = useFaceDetection();

  const [cameraStarted, setCameraStarted] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);

  // Start camera when models are loaded
  useEffect(() => {
    if (isModelLoaded && autoStart && !cameraStarted) {
      startCamera()
        .then(() => setCameraStarted(true))
        .catch(err => {
          setCaptureError(err.message || 'Failed to start camera');
          onError?.(err.message || 'Failed to start camera');
        });
    }
  }, [isModelLoaded, autoStart, cameraStarted, startCamera, onError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  // Continuous face detection for preview
  useEffect(() => {
    if (!cameraStarted || !showPreview) return;

    let animationFrame: number;
    let detecting = false;

    const detect = async () => {
      if (!detecting) {
        detecting = true;
        const result = await detectFace();
        setFaceDetected(result.detected);
        detecting = false;
      }
      animationFrame = requestAnimationFrame(detect);
    };

    detect();

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [cameraStarted, showPreview, detectFace]);

  const handleCapture = useCallback(async () => {
    setCapturing(true);
    setCaptureError(null);

    try {
      const result = await detectFace();

      if (!result.detected || !result.descriptor) {
        const errorMsg = 'No face detected. Please position your face in the camera.';
        setCaptureError(errorMsg);
        onError?.(errorMsg);
        return;
      }

      const embedding = getEmbeddingArray(result.descriptor);
      onCapture(embedding);
    } catch (err) {
      const errorMsg = (err as Error).message || 'Failed to capture face';
      setCaptureError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setCapturing(false);
    }
  }, [detectFace, getEmbeddingArray, onCapture, onError]);

  const handleRetry = useCallback(() => {
    setCaptureError(null);
    if (!cameraStarted) {
      startCamera()
        .then(() => setCameraStarted(true))
        .catch(err => {
          setCaptureError(err.message || 'Failed to start camera');
        });
    }
  }, [cameraStarted, startCamera]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading face detection models...
        </Typography>
      </Box>
    );
  }

  if (modelError) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <ErrorIcon color="error" sx={{ fontSize: 48 }} />
        <Typography variant="body1" color="error" sx={{ mt: 2, textAlign: 'center' }}>
          {modelError}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Reload Page
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Camera Preview */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: 400,
          aspectRatio: '4/3',
          backgroundColor: 'grey.900',
          borderRadius: 2,
          overflow: 'hidden',
          mb: 2
        }}
      >
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transform: 'scaleX(-1)' // Mirror for selfie
          }}
        />
        {showPreview && (
          <canvas
            ref={canvasRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              transform: 'scaleX(-1)' // Mirror to match video
            }}
          />
        )}

        {/* Status Overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            right: 8,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          {faceDetected ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'rgba(76, 175, 80, 0.9)',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: 2
              }}
            >
              <CheckCircle sx={{ fontSize: 20, mr: 1 }} />
              <Typography variant="body2">Face Detected</Typography>
            </Box>
          ) : cameraStarted ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 152, 0, 0.9)',
                color: 'white',
                px: 2,
                py: 0.5,
                borderRadius: 2
              }}
            >
              <CameraAlt sx={{ fontSize: 20, mr: 1 }} />
              <Typography variant="body2">Position your face</Typography>
            </Box>
          ) : null}
        </Box>
      </Box>

      {/* Error Message */}
      {captureError && (
        <Alert severity="error" sx={{ mb: 2, width: '100%', maxWidth: 400 }}>
          {captureError}
        </Alert>
      )}

      {/* Capture Button */}
      <Button
        variant="contained"
        size="large"
        onClick={captureError ? handleRetry : handleCapture}
        disabled={!cameraStarted || capturing || (!faceDetected && !captureError)}
        startIcon={captureError ? <Refresh /> : <CameraAlt />}
        sx={{ minWidth: 200 }}
      >
        {capturing ? (
          <CircularProgress size={24} color="inherit" />
        ) : captureError ? (
          'Retry'
        ) : (
          captureButtonText
        )}
      </Button>

      {/* Instructions */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 2, textAlign: 'center', maxWidth: 300 }}
      >
        Position your face clearly in the camera frame. Ensure good lighting and remove any obstructions.
      </Typography>
    </Box>
  );
}
